/* P-182: la carga masiva de vacaciones solicitadas desde la planilla de Natalia.
 *
 * Lo PURO: emparejar cada fila de la planilla con una persona de la nómina,
 * validar las fechas y armar la licencia. La lectura del .xls y el insert viven
 * en la pantalla y el servicio; acá está lo que decide bien o mal.
 *
 * ── POR QUÉ NO ALCANZA EL LEGAJO ─────────────────────────────────────────────
 *
 * El legajo NO es una clave estable: el legajo 9 de la planilla es CORREA, pero
 * en la nómina el 9 es PAULETTI —otra persona—; y hay legajos con dos personas
 * (el 33 es RAUPENSTRAUCH y también JACINTO GLAZER). Emparejar sólo por número
 * le cargaría las vacaciones de uno a otro. Por eso se exige que COINCIDA
 * TAMBIÉN EL APELLIDO, y ante la duda no se empareja: se muestra para que RRHH
 * lo resuelva. Es la misma lección de la geocerca — no adivinar a quién.
 */

/* Mayúsculas, sin acentos, sin dobles espacios. Para comparar nombres. Se
 * conserva la Ñ (es una letra propia, no una N con tilde): partiendo por Ñ, su
 * tilde no cae en el barrido de acentos y «IBAÑEZ» no queda «IBANEZ». */
export function norm(s) {
  return String(s ?? "")
    .toUpperCase()
    .split("Ñ")
    .map((p) => p.normalize("NFD").replace(/[̀-ͯ]/g, ""))
    .join("Ñ")
    .replace(/\s+/g, " ")
    .trim();
}

/* El apellido de un «APELLIDO, NOMBRE»: lo de antes de la coma. Si no hay
 * coma, la cadena entera (algunas planillas no la ponen). */
export function apellidoDe(nombre) {
  const s = String(nombre ?? "");
  return norm(s.includes(",") ? s.split(",")[0] : s);
}

/* A ISO «YYYY-MM-DD». Acepta un Date (lo que da SheetJS con cellDates), un
 * serial de Excel (número, epoch 1899-12-30) o una cadena ya ISO. `null` si no
 * se puede — una fecha que no se entiende no se inventa. */
export function aIso(v) {
  if (v == null || v === "") return null;
  if (v instanceof Date && !isNaN(v)) {
    return `${v.getFullYear()}-${String(v.getMonth() + 1).padStart(2, "0")}-${String(v.getDate()).padStart(2, "0")}`;
  }
  if (typeof v === "number" && isFinite(v)) {
    // Serial de Excel → fecha. 25569 = 1970-01-01; *86400s.
    const d = new Date(Math.round((v - 25569) * 86400 * 1000));
    if (isNaN(d)) return null;
    return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, "0")}-${String(d.getUTCDate()).padStart(2, "0")}`;
  }
  const s = String(v).trim();
  return /^\d{4}-\d{2}-\d{2}/.test(s) ? s.slice(0, 10) : null;
}

/* El último día de vacaciones = el regreso menos un día. La planilla trae el
 * día que VUELVE a trabajar; en licencias `hasta` es el último día afuera, y
 * `diasCorridos` cuenta las dos puntas. `null` si el regreso no es una fecha. */
export function ultimoDia(regresoIso) {
  if (!regresoIso) return null;
  const d = new Date(regresoIso + "T00:00:00Z");
  if (isNaN(d)) return null;
  d.setUTCDate(d.getUTCDate() - 1);
  return d.toISOString().slice(0, 10);
}

/* Empareja una fila con la nómina. `porLegajo` es `{legajo: [{dni, apellido}]}`.
 * Devuelve `{estado, dni, persona}`:
 *   · 'ok'          — un solo candidato cuyo apellido coincide.
 *   · 'sin_legajo'  — la fila no trae legajo.
 *   · 'sin_persona' — ese legajo no está en la nómina.
 *   · 'otro_legajo' — el legajo está, pero es de otra persona (no coincide el
 *                     apellido). Es el caso peligroso: cargar acá sería cargarle
 *                     las vacaciones al que NO es.
 *   · 'ambiguo'     — más de un candidato coincide (no se elige).
 */
export function emparejar(fila, porLegajo) {
  const leg = String(fila?.legajo ?? "").trim();
  if (!leg) return { estado: "sin_legajo", dni: null, persona: null };
  const cands = (porLegajo || {})[leg] || [];
  if (!cands.length) return { estado: "sin_persona", dni: null, persona: null };
  const ape = apellidoDe(fila?.nombre);
  const primero = ape.split(" ")[0];
  const hit = cands.filter(
    (c) => primero && norm(c.apellido).startsWith(primero),
  );
  if (hit.length === 1) {
    return { estado: "ok", dni: String(hit[0].dni), persona: hit[0] };
  }
  if (hit.length > 1) return { estado: "ambiguo", dni: null, persona: null };
  return { estado: "otro_legajo", dni: null, persona: cands[0] };
}

/* Valida las fechas de la fila. `null` si están bien, o un código de error:
 * 'falta_salida' | 'falta_regreso' | 'regreso_no_posterior'. */
export function validarFechas(salidaIso, regresoIso) {
  if (!salidaIso) return "falta_salida";
  if (!regresoIso) return "falta_regreso";
  if (regresoIso <= salidaIso) return "regreso_no_posterior";
  return null;
}

/* La clave de deduplicado: misma persona + mismo día de salida. Re-subir la
 * planilla no duplica lo ya cargado. */
export function claveDedup(dni, salidaIso) {
  return `${String(dni)}|${salidaIso}`;
}

/* Clasifica la fila entera contra la nómina y lo ya cargado. Devuelve
 * `{estado, dni, persona, desde, hasta}` con un `estado` de:
 *   'lista' (se inserta) | 'duplicada' | 'sin_persona' | 'otro_legajo' |
 *   'ambiguo' | 'sin_legajo' | 'falta_salida' | 'falta_regreso' |
 *   'regreso_no_posterior'.
 */
export function clasificar(fila, porLegajo, yaCargadas) {
  const m = emparejar(fila, porLegajo);
  const desde = aIso(fila?.salida);
  const regreso = aIso(fila?.regreso);
  if (m.estado !== "ok") {
    return { ...m, desde, hasta: ultimoDia(regreso) };
  }
  const errF = validarFechas(desde, regreso);
  if (errF) {
    return { estado: errF, dni: m.dni, persona: m.persona, desde, hasta: null };
  }
  const hasta = ultimoDia(regreso);
  const dup = yaCargadas instanceof Set && yaCargadas.has(claveDedup(m.dni, desde));
  return {
    estado: dup ? "duplicada" : "lista",
    dni: m.dni, persona: m.persona, desde, hasta,
  };
}

/* Los rótulos legibles de cada estado, para la pantalla. */
export const ROTULO_ESTADO = {
  lista: "Lista para cargar",
  duplicada: "Ya estaba cargada",
  sin_persona: "No encontré ese legajo en la nómina",
  otro_legajo: "Ese legajo es de otra persona en la nómina",
  ambiguo: "Hay más de una persona con ese legajo",
  sin_legajo: "La fila no trae legajo",
  falta_salida: "Falta la fecha de salida",
  falta_regreso: "Falta la fecha de regreso",
  regreso_no_posterior: "El regreso no es posterior a la salida",
};
